import { z } from "zod";
import type { EgressPolicy } from "./tools.ts";

/**
 * Code execution (openspec change `agent-turn`, D14–D21a): the agent runs a short program in an
 * isolated sandbox. Core declares the `run_code` tool; providers (`AgentCoreCodeInterpreterProvider`
 * in `@esi/aws`) run it. These schemas are what the model sends and what any provider returns:
 * no identifier of a tenant, user, project or conversation crosses them.
 */

/** The model-facing name of the code execution tool (its UI parts are `tool-run_code`). */
export const RUN_CODE_TOOL_NAME = "run_code";

/** The settings key suffix of code execution (`tools.codeExecution`). */
export const CODE_EXECUTION_TOOL_ID = "codeExecution";

/** The languages a program may be written in. */
export const RUN_CODE_LANGUAGES = ["python", "javascript"] as const;
export type RunCodeLanguage = (typeof RUN_CODE_LANGUAGES)[number];

/** Longest program (D14). */
export const RUN_CODE_MAX_CODE_CHARS = 20_000;
/** Longest note on what the run is for, shown in the step row. */
export const RUN_CODE_MAX_NOTE_CHARS = 120;
/** Output kept for the model: the rest is clipped and the model told (D16). */
export const RUN_CODE_MAX_STDOUT_CHARS = 20_000;
export const RUN_CODE_MAX_STDERR_CHARS = 4_000;
/** Longest one call may run (D16): the wrapper's own timeout for this tool. */
export const RUN_CODE_TIMEOUT_MS = 120_000;
/** Most code calls in one turn, counted within the turn's outbound calls (D16). */
export const MAX_CODE_CALLS_PER_TURN = 6;
/** Most file handles one call may name (D18, slice 5). */
export const RUN_CODE_MAX_FILES = 10;
/** Largest file copied into the sandbox, and most bytes one call copies (D18). */
export const RUN_CODE_MAX_FILE_IN_BYTES = 25 * 1024 * 1024;
export const RUN_CODE_MAX_CALL_IN_BYTES = 60 * 1024 * 1024;
/** The folder a program writes the files to keep (relative to its working directory, D18). */
export const RUN_CODE_OUTPUT_DIR = "out";

/** A handle a call may name: a document or attachment (`D3`) or a text artefact (`A2`). */
export const runCodeFileHandleSchema = z
  .string()
  .regex(/^\s*[DA][1-9][0-9]{0,8}\s*$/i, "a D# or A# handle");

/** What the model sends. */
export const runCodeInputSchema = z.strictObject({
  language: z.enum(RUN_CODE_LANGUAGES),
  code: z.string().min(1).max(RUN_CODE_MAX_CODE_CHARS),
  note: z.string().trim().max(RUN_CODE_MAX_NOTE_CHARS).optional(),
  /** Files copied into the working directory before the run (D18). */
  files: z.array(runCodeFileHandleSchema).min(1).max(RUN_CODE_MAX_FILES).optional(),
});
export type RunCodeInput = z.output<typeof runCodeInputSchema>;

/** One file the server copies into the session: its sanitised name and bytes, nothing else. */
export interface SandboxFile {
  name: string;
  bytes: Uint8Array;
}

/** What a provider receives: the program and the files' bytes, never a handle or an ID. */
export interface SandboxRunInput {
  language: RunCodeLanguage;
  code: string;
  files?: readonly SandboxFile[];
}

// Any Uint8Array (an SDK may hand a view over a shared buffer).
const bytesSchema = z.custom<Uint8Array>((value) => value instanceof Uint8Array);

/** A file the program left in its output folder, or an image it printed (D18). */
export const sandboxOutputFileSchema = z.strictObject({
  name: z.string().min(1).max(1_024),
  /** The size the sandbox listed; bytes are absent when it was not fetched (over a limit). */
  size: z.int().min(0),
  mime: z.string().max(255).optional(),
  bytes: bytesSchema.optional(),
});
export type SandboxOutputFile = z.infer<typeof sandboxOutputFileSchema>;

/**
 * What a run kept, as the model and the step row read it: each file artefact by handle, and
 * what was dropped and why (`type`, `size`, `count`, `artefacts_off`, `limit`, `failed`).
 */
export const runCodeKeptSchema = z.strictObject({
  files: z.array(
    z.strictObject({
      artefact: z.string(),
      version: z.int().min(1),
      name: z.string(),
      size: z.int().min(0),
      mime: z.string(),
      image: z.boolean().optional(),
    }),
  ),
  dropped: z.array(z.strictObject({ name: z.string(), reason: z.string() })),
});
export type RunCodeKept = z.infer<typeof runCodeKeptSchema>;

/**
 * What any provider returns for one run. Lengths are bounded generously here (a provider clips
 * first); the declaration clips again to what the model reads.
 */
export const runCodeOutputSchema = z.strictObject({
  stdout: z.string().max(200_000),
  stderr: z.string().max(200_000),
  exitCode: z.int(),
  /**
   * The program's run on the provider's clock, around its execution call. Never a duration the
   * sandbox reports: AgentCore reported 0 for a real run of several seconds (#442).
   */
  durationMs: z.int().min(0),
  /** The provider's opaque session ID, for the audit trail only: never shown to the model. */
  sessionId: z.string().max(256).optional(),
  /** Files in the output folder (bytes for those fetched), D18. */
  files: z.array(sandboxOutputFileSchema).max(100).optional(),
  /** Images the program printed (`image/png`), D18. */
  images: z.array(sandboxOutputFileSchema).max(20).optional(),
  /** What the server kept of them: set after the run, bytes gone (core, never a provider). */
  kept: runCodeKeptSchema.optional(),
});
export type RunCodeOutput = z.infer<typeof runCodeOutputSchema>;

/**
 * Closed codes of a code call that did not run, beside the platform's (`turn_limit`, …):
 * `session_unavailable`: the sandbox session could not start (nothing metered).
 */
export const RUN_CODE_FAILURES = ["timeout", "session_unavailable"] as const;
export type RunCodeFailure = (typeof RUN_CODE_FAILURES)[number];

/**
 * The default `tools.codeExecution.egress` (Q3, D17): the floors policy of an isolated sandbox.
 * Nothing leaves the account or the Region, so Public, Internal and Confidential data run without
 * a prompt and Restricted after the creator approves the exact code.
 */
export const DEFAULT_CODE_EXECUTION_EGRESS_POLICY: EgressPolicy = {
  project: "allow",
  entity: "allow",
  named: "confirm",
};
